(() => {
  if (window.__relatedProductsFigmaInitialized) return;
  window.__relatedProductsFigmaInitialized = true;

  const CARD_SELECTOR = '.related-products-figma__card';
  const SCROLLER_SELECTOR = '[data-related-products-scroll]';
  const IMAGE_SELECTOR = '.related-products-figma__image';

  const initializeProductImage = (image) => {
    if (!(image instanceof HTMLImageElement) || image.dataset.relatedImageReady === 'true') return;

    image.dataset.relatedImageReady = 'true';
    const fallbackSrc = image.getAttribute('src');

    const handleLoad = () => {
      image.classList.remove('is-image-retrying', 'is-image-error');
    };

    const handleError = () => {
      const retryCount = Number.parseInt(image.dataset.relatedImageRetryCount || '0', 10);

      if (!fallbackSrc || retryCount >= 1) {
        image.classList.remove('is-image-retrying');
        image.classList.add('is-image-error');
        return;
      }

      image.classList.add('is-image-retrying');
      image.dataset.relatedImageRetryCount = String(retryCount + 1);
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.loading = 'eager';

      window.setTimeout(() => {
        image.src = fallbackSrc;
      }, 150);
    };

    image.addEventListener('load', handleLoad);
    image.addEventListener('error', handleError);

    if (image.complete) {
      if (image.naturalWidth > 0) {
        handleLoad();
      } else {
        handleError();
      }
    }
  };

  const initializeProductImages = (root = document) => {
    const images = root.matches?.(IMAGE_SELECTOR)
      ? [root]
      : Array.from(root.querySelectorAll?.(IMAGE_SELECTOR) || []);

    images.forEach(initializeProductImage);
  };

  const getCards = (scroller) => Array.from(scroller.querySelectorAll(CARD_SELECTOR));

  const getCardOffset = (scroller, card) => card.offsetLeft - scroller.offsetLeft;

  const getCurrentIndex = (scroller, cards = getCards(scroller)) => {
    if (!cards.length) return 0;

    return cards.reduce(
      (closest, card, index) => {
        const distance = Math.abs(getCardOffset(scroller, card) - scroller.scrollLeft);
        return distance < closest.distance ? { index, distance } : closest;
      },
      { index: 0, distance: Number.POSITIVE_INFINITY }
    ).index;
  };

  const getScrollButtons = (scroller) => {
    if (!scroller.id) return { prevButtons: [], nextButtons: [] };

    const buttons = Array.from(document.querySelectorAll('[data-related-products-scroll-target]')).filter(
      (button) => button.getAttribute('data-related-products-scroll-target') === scroller.id
    );

    return {
      prevButtons: buttons.filter((button) => button.hasAttribute('data-related-products-scroll-prev')),
      nextButtons: buttons.filter((button) => button.hasAttribute('data-related-products-scroll-next'))
    };
  };

  const updateScrollButtons = (scroller) => {
    const cards = getCards(scroller);
    const currentIndex = getCurrentIndex(scroller, cards);
    const lastIndex = Math.max(cards.length - 1, 0);
    const { prevButtons, nextButtons } = getScrollButtons(scroller);

    prevButtons.forEach((button) => {
      button.disabled = currentIndex <= 0;
      button.setAttribute('aria-disabled', String(currentIndex <= 0));
    });

    nextButtons.forEach((button) => {
      button.disabled = currentIndex >= lastIndex;
      button.setAttribute('aria-disabled', String(currentIndex >= lastIndex));
    });
  };

  const scrollToCard = (scroller, index) => {
    const cards = getCards(scroller);
    if (!cards.length) return;

    const clampedIndex = Math.max(0, Math.min(index, cards.length - 1));
    scroller.scrollTo({
      left: getCardOffset(scroller, cards[clampedIndex]),
      behavior: 'smooth'
    });

    window.setTimeout(() => updateScrollButtons(scroller), 260);
  };

  const initializeScrollers = (resetPosition = false) => {
    document.querySelectorAll(SCROLLER_SELECTOR).forEach((scroller) => {
      const cards = getCards(scroller);
      if (!cards.length) return;

      if (resetPosition) {
        scroller.scrollTo({ left: getCardOffset(scroller, cards[0]) });
      }

      updateScrollButtons(scroller);
    });
  };

  let scrollerUpdateFrame = null;

  const requestScrollerUpdate = (resetPosition = false) => {
    if (scrollerUpdateFrame) {
      window.cancelAnimationFrame(scrollerUpdateFrame);
    }

    scrollerUpdateFrame = window.requestAnimationFrame(() => {
      initializeScrollers(resetPosition);
      initializeProductImages();
      scrollerUpdateFrame = null;
    });
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-related-quick-open]');
    const scrollButton = event.target.closest('[data-related-products-scroll-prev], [data-related-products-scroll-next]');

    if (scrollButton) {
      event.preventDefault();
      if (scrollButton.disabled) return;

      const targetId = scrollButton.getAttribute('data-related-products-scroll-target');
      const scroller = targetId ? document.getElementById(targetId) : null;
      if (!scroller) return;

      const direction = scrollButton.hasAttribute('data-related-products-scroll-next') ? 1 : -1;
      scrollToCard(scroller, getCurrentIndex(scroller) + direction);
      return;
    }

    if (!opener) return;

    event.preventDefault();

    const templateId = opener.getAttribute('data-related-quick-template');
    const modalId = opener.getAttribute('data-related-quick-modal');
    if (!templateId || !modalId) return;

    let modal = document.getElementById(modalId);

    if (!modal) {
      const template = document.getElementById(templateId);
      const modalNode = template?.content?.firstElementChild?.cloneNode(true);
      if (!modalNode) return;

      document.body.appendChild(modalNode);
      modal = document.getElementById(modalId);
    }

    if (modal && typeof modal.show === 'function') {
      modal.show(opener);
    }
  });

  document.addEventListener(
    'scroll',
    (event) => {
      if (event.target?.matches?.(SCROLLER_SELECTOR)) {
        updateScrollButtons(event.target);
      }
    },
    true
  );

  if ('MutationObserver' in window) {
    const observer = new MutationObserver((mutations) => {
      const addedElements = mutations.flatMap((mutation) =>
        Array.from(mutation.addedNodes).filter((node) => node.nodeType === Node.ELEMENT_NODE)
      );
      const shouldUpdate = addedElements.some(
        (node) => node.matches?.(SCROLLER_SELECTOR) || node.querySelector?.(SCROLLER_SELECTOR)
      );

      addedElements.forEach(initializeProductImages);

      if (shouldUpdate) {
        requestScrollerUpdate(false);
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  window.addEventListener('resize', () => requestScrollerUpdate(false));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => requestScrollerUpdate(true), { once: true });
  } else {
    requestScrollerUpdate(true);
  }

  initializeProductImages();
})();
